import React, { useEffect, useRef, useState } from 'react';
import { api } from './api.js';
import { useLang } from './i18n.js';
import { relTime } from './money.js';
import { Empty, Spinner } from './ui.jsx';

/**
 * The notification bell.
 *
 * The `notifications` table has been written since B1 — task assignment, deal
 * reassignment, company handover — and until now nothing read it. `/bootstrap`
 * has been returning an unread count with no surface behind it.
 *
 * Links are stored as paths (`/deals/D-0007`, `/clients/CO-0004`, `/tasks`)
 * because the server has no opinion about how this client routes. This app
 * opens records in drawers rather than at URLs, so the path is translated into
 * the action that actually shows the record. Rendering them as <a href> would
 * produce links that 404 on a full page load.
 */
function resolve(link) {
  if (!link) return null;
  const deal = link.match(/^\/deals\/(.+)$/);
  if (deal) return { kind: 'deal', id: deal[1] };
  const client = link.match(/^\/clients\/(.+)$/);
  if (client) return { kind: 'client', id: client[1] };
  if (link.startsWith('/tasks')) return { kind: 'route', id: 'today' };
  return null;
}

const DOT = {
  task: 'var(--warning)',
  deal: 'var(--entity-btm)',
  company: 'var(--entity-mr)',
  graduation: 'var(--success)',
  system: 'var(--ink-3)',
};

export function NotificationBell({ unread, onOpenDeal, onOpenClient, onRoute, onChanged }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const wrap = useRef(null);

  // Fetch on open, not on mount: the badge already comes from /bootstrap, and a
  // list nobody has asked to see is a request per page load for nothing.
  useEffect(() => {
    if (!open) return;
    setData(null);
    setError('');
    api
      .notifications({ limit: 30 })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markRead = async (ids) => {
    // Optimistic locally, then reload the shell so the badge agrees with the
    // server rather than with our guess.
    setData((d) =>
      d
        ? {
            ...d,
            notifications: d.notifications.map((n) =>
              !ids || ids.includes(n.id) ? { ...n, unread: 0 } : n,
            ),
            unread: ids ? Math.max(0, d.unread - ids.length) : 0,
          }
        : d,
    );
    try {
      await api.markNotificationsRead(ids);
      onChanged?.();
    } catch {
      /* the badge will correct itself on the next bootstrap */
    }
  };

  const activate = (n) => {
    if (n.unread) markRead([n.id]);
    const target = resolve(n.link);
    if (!target) return;
    setOpen(false);
    if (target.kind === 'deal') onOpenDeal(target.id);
    else if (target.kind === 'client') onOpenClient(target.id);
    else onRoute(target.id);
  };

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <button
        className="icon-btn"
        aria-label={t('notifications')}
        title={t('notifications')}
        onClick={() => setOpen((v) => !v)}
        style={{ position: 'relative', width: 32, height: 32 }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8 1.6a4 4 0 0 0-4 4v2.3L2.8 10.4A.5.5 0 0 0 3.25 11h9.5a.5.5 0 0 0 .45-.6L12 7.9V5.6a4 4 0 0 0-4-4Z" />
          <path d="M6.4 13a1.7 1.7 0 0 0 3.2 0" />
        </svg>
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 1,
              right: 1,
              minWidth: 14,
              height: 14,
              padding: '0 3px',
              borderRadius: 7,
              background: 'var(--danger)',
              color: '#fff',
              fontSize: 9.5,
              fontWeight: 700,
              lineHeight: '14px',
              textAlign: 'center',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 330,
            maxHeight: 420,
            overflow: 'auto',
            background: 'var(--raised)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-lg)',
            boxShadow: 'var(--shadow-3)',
            zIndex: 60,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              borderBottom: '1px solid var(--line)',
              position: 'sticky',
              top: 0,
              background: 'var(--raised)',
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 13 }}>{t('notifications')}</span>
            {data?.unread > 0 && (
              <button
                className="btn btn-sm"
                style={{ marginLeft: 'auto' }}
                onClick={() => markRead(undefined)}
              >
                {t('markAllRead')}
              </button>
            )}
          </div>

          {error && <div style={{ padding: 14, fontSize: 13 }}>{error}</div>}
          {!error && !data && <Spinner label={t('loading')} />}

          {data && data.notifications.length === 0 && <Empty>{t('noNotifications')}</Empty>}

          {data?.notifications.map((n) => {
            const clickable = Boolean(resolve(n.link));
            return (
              <div
                key={n.id}
                onClick={() => activate(n)}
                style={{
                  display: 'flex',
                  gap: 9,
                  padding: '10px 12px',
                  borderBottom: '1px solid var(--line)',
                  cursor: clickable || n.unread ? 'pointer' : 'default',
                  background: n.unread ? 'var(--primary-soft)' : 'transparent',
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    marginTop: 5,
                    flexShrink: 0,
                    background: DOT[n.type] || DOT.system,
                  }}
                />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 13,
                      fontWeight: n.unread ? 700 : 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {n.title}
                  </span>
                  {n.body && (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 12,
                        color: 'var(--ink-2)',
                        marginTop: 1,
                      }}
                    >
                      {n.body}
                    </span>
                  )}
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
                    {relTime(n.created_at, lang)}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
